import clsx from 'clsx';
import type { ReactNode } from 'react';

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('mx-auto w-full max-w-[1440px] px-4 pt-6 pb-16 md:px-8 md:pt-8', className)}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink md:text-[26px] md:leading-[34px]">
          {title}
        </h1>
        {subtitle ? <div className="mt-1 text-[14px] text-ink-2">{subtitle}</div> : null}
        {children}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  meta,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={clsx('rounded-lg border border-line bg-surface', className)}>
      {title || actions ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line px-5 py-3.5">
          {/* Пояснение переносится под заголовок, а кнопки — под пояснение: на узком экране ничего не уходит вбок */}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            {title ? (
              <h2 className="text-[16px] leading-6 font-semibold [overflow-wrap:anywhere] text-ink">
                {title}
              </h2>
            ) : null}
            {meta ? <span className="text-[13px] text-ink-2">{meta}</span> : null}
          </div>
          {actions ? <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={clsx(bodyClassName ?? 'p-5')}>{children}</div>
    </section>
  );
}

/** Полоса показателей в духе штампа чертежа: подпись сверху, значение крупно, пояснение снизу. */
export function TitleBlock({
  items,
  className,
}: {
  items: Array<{
    label: string;
    value: ReactNode;
    note?: ReactNode;
    tone?: 'default' | 'accent' | 'danger' | 'success';
  }>;
  className?: string;
}) {
  return (
    <div
      className={clsx('title-block grid-cols-1', className)}
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))` }}
    >
      {items.map((it) => (
        <div key={it.label} className="flex min-w-0 flex-col gap-1 px-5 py-4">
          <span className="text-[13px] text-ink-2">{it.label}</span>
          <span
            className={clsx(
              'num text-[26px] leading-8 font-semibold tracking-[-0.01em]',
              it.tone === 'accent' && 'text-accent-text',
              it.tone === 'danger' && 'text-danger',
              it.tone === 'success' && 'text-success',
              (!it.tone || it.tone === 'default') && 'text-ink',
            )}
          >
            {it.value}
          </span>
          {it.note ? <span className="text-[13px] leading-[18px] text-ink-2">{it.note}</span> : null}
        </div>
      ))}
    </div>
  );
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[minmax(140px,max-content)_1fr]">
      {items.map((it) => (
        <div key={it.label} className="contents">
          <dt className="text-[13px] text-ink-2">{it.label}</dt>
          <dd className="text-[14px] text-ink">{it.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
